import React, { useState } from 'react';
import { User, BellRing, FileText, Briefcase } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, updatePassword } from '../lib/firebase/auth';
import Button from '../components/common/Button';
import JobAlertSettings from '../components/profile/JobAlertSettings';
import CoverLetterManager from '../components/profile/CoverLetterManager';
import MyApplications from '../components/profile/MyApplications';

type TabKey = 'account' | 'alerts' | 'cover-letter' | 'applications';

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'account', label: 'Account', icon: User },
  { key: 'alerts', label: 'Job Alerts', icon: BellRing },
  { key: 'cover-letter', label: 'Cover Letter', icon: FileText },
  { key: 'applications', label: 'My Applications', icon: Briefcase },
];

export default function Profile() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('account');
  const [name, setName] = useState(user?.user_metadata?.name || user?.email?.split('@')[0] || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (name !== (user?.user_metadata?.name || '')) {
        const { error } = await updateProfile({ displayName: name });
        if (error) throw error;
      }

      if (password) {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        const { error } = await updatePassword(password);
        if (error) throw error;
      }

      setSuccess('Profile updated successfully');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account, alerts, cover letter, and applications</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-56 flex-shrink-0">
          <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {TABS.find((t) => t.key === activeTab)?.label}
              </h2>
            </div>

            <div className="p-6">
              {activeTab === 'account' && (
                <AccountSettings
                  user={user}
                  name={name}
                  setName={setName}
                  password={password}
                  setPassword={setPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  loading={loading}
                  error={error}
                  success={success}
                  onSubmit={handleSubmit}
                />
              )}

              {activeTab === 'alerts' && <JobAlertSettings />}
              {activeTab === 'cover-letter' && <CoverLetterManager />}
              {activeTab === 'applications' && <MyApplications />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountSettings({
  user,
  name,
  setName,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  loading,
  error,
  success,
  onSubmit,
}: {
  user: any;
  name: string;
  setName: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  confirmPassword: string;
  setConfirmPassword: (v: string) => void;
  loading: boolean;
  error: string | null;
  success: string | null;
  onSubmit: (e: React.FormEvent) => void;
}) {
  return (
    <div className="max-w-lg">
      <form onSubmit={onSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            {success}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 bg-gray-50 text-sm text-gray-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-green-500 focus:border-green-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-green-500 focus:border-green-500"
            placeholder="Leave blank to keep current password"
          />
        </div>

        {password && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-green-500 focus:border-green-500"
            />
          </div>
        )}

        <Button type="submit" variant="primary" isLoading={loading} className="w-full">
          Save Changes
        </Button>
      </form>
    </div>
  );
}
